import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Settings } from 'lucide-react';
import { icons, LucideIcon } from 'lucide-react';
import { HexColorPicker } from 'react-colorful';
import { useNavigate, useParams } from 'react-router-dom';
import { EventTypeUpsertData } from '@/types';
import { useEventTypes } from '@/hooks/useEventTypes';
import { eventTypeService } from '@/services/api';
import { EventTypeIconPicker } from '@/components/features/platform/EventTypeIconPicker';
import { LoadingOverlay } from '@/components/shared/LoadingOverlay';

const initialForm: EventTypeUpsertData & { code: string } = {
  code: '',
  description: '',
  labelEs: '',
  labelEn: '',
  icon: 'CircleUserRound',
  bgColor: '#e5e7eb',
  textColor: '#374151',
  iconColor: '#374151',
  active: true,
};

const inputClassName =
  'w-full border border-gray-300 rounded-md px-3 py-2 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange';

export const EventTypeForm = () => {
  const navigate = useNavigate();
  const { id: editId } = useParams<{ id: string }>();
  const isEditMode = !!editId;
  const { eventTypes, reloadEventTypes, loading } = useEventTypes();
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [openColorPicker, setOpenColorPicker] = useState<'bgColor' | 'textColor' | 'iconColor' | null>(
    null
  );
  const colorPopoverRef = useRef<HTMLDivElement | null>(null);

  const currentItem = useMemo(
    () => (isEditMode ? eventTypes.find((it) => it.id === editId) : null),
    [eventTypes, editId, isEditMode]
  );

  const PreviewIcon =
    (icons[(form.icon || 'CircleUserRound') as keyof typeof icons] as LucideIcon) || icons.CircleUserRound;
  const contrastRatio = useMemo(() => getContrastRatio(form.textColor, form.bgColor), [form.textColor, form.bgColor]);
  const hasLowContrast = contrastRatio < 4.5;

  useEffect(() => {
    if (!isEditMode || !currentItem) return;
    setForm({
      code: currentItem.code,
      description: currentItem.description,
      labelEs: currentItem.labelEs,
      labelEn: currentItem.labelEn,
      icon: currentItem.icon,
      bgColor: currentItem.bgColor,
      textColor: currentItem.textColor,
      iconColor: currentItem.iconColor,
      active: currentItem.active,
    });
  }, [currentItem, isEditMode]);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!openColorPicker) return;
      const target = event.target as Node;
      if (colorPopoverRef.current && !colorPopoverRef.current.contains(target)) {
        setOpenColorPicker(null);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [openColorPicker]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEditMode && currentItem) {
        await eventTypeService.update(currentItem.id, {
          description: form.description,
          labelEs: form.labelEs,
          labelEn: form.labelEn,
          icon: form.icon,
          bgColor: form.bgColor,
          textColor: form.textColor,
          iconColor: form.iconColor,
        });
      } else {
        await eventTypeService.create(form);
      }
      await reloadEventTypes();
      navigate('/platform/event-types');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-full bg-brand-white py-4">
      <LoadingOverlay
        isVisible={loading || saving}
        message={saving ? 'Guardando tipo de evento...' : 'Cargando tipos de eventos...'}
      />

      <div className={`max-w-7xl px-8 mx-auto space-y-6 ${loading || saving ? 'blur-sm pointer-events-none' : ''}`}>
        <div className="flex flex-wrap justify-between items-center gap-3">
          <div className="flex items-center gap-3">
            <Settings className="h-7 w-7 text-brand-orange" strokeWidth={1.5} />
            <h1 className="text-3xl font-bold text-brand-orange">Gestión de tipos de eventos</h1>
          </div>
          <div />
        </div>

        <form onSubmit={onSubmit} className="bg-brand-white p-0 rounded-lg shadow-lg border border-gray-200">
          <h2 className="text-xl font-semibold text-brand-white bg-brand-gray-dark p-4 rounded-t-lg">
            {isEditMode ? 'Editar tipo de evento' : 'Crear tipo de evento'}
          </h2>
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-brand-gray-dark">Código (3 caracteres)</label>
                <input
                  className={`${inputClassName} ${isEditMode ? '!bg-gray-100 !text-gray-500 cursor-not-allowed' : ''}`}
                  placeholder="SUB"
                  value={form.code}
                  maxLength={3}
                  disabled={isEditMode}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }))
                  }
                  required
                />
                <p className="mt-1 text-xs text-gray-500">Solo letras y números, exactamente 3 caracteres.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-gray-dark">Descripción interna</label>
                <input
                  className={inputClassName}
                  placeholder="Descripción interna para operación"
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  required
                />
                <p className="mt-1 text-xs text-gray-500">Solo referencia operativa, no visible en las tablas.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-gray-dark">Etiqueta visible en UI (ES)</label>
                <input
                  className={inputClassName}
                  value={form.labelEs}
                  onChange={(e) => setForm((p) => ({ ...p, labelEs: e.target.value }))}
                  required
                />
                <p className="mt-1 text-xs text-gray-500">Texto que verá el usuario en español.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-gray-dark">Etiqueta visible en UI (EN)</label>
                <input
                  className={inputClassName}
                  value={form.labelEn}
                  onChange={(e) => setForm((p) => ({ ...p, labelEn: e.target.value }))}
                  required
                />
                <p className="mt-1 text-xs text-gray-500">Texto que verá el usuario en inglés.</p>
              </div>
            </div>

            <EventTypeIconPicker
              value={form.icon}
              onChange={(iconName) => setForm((prev) => ({ ...prev, icon: iconName }))}
            />
            <p className="text-xs text-gray-500 -mt-3">
              Podés explorar todos los íconos en{' '}
              <a
                href="https://lucide.dev/icons/"
                target="_blank"
                rel="noreferrer"
                className="text-brand-orange underline"
              >
                lucide.dev/icons
              </a>
              . La búsqueda funciona por término en inglés.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-start" ref={colorPopoverRef}>
              <div className="rounded-lg border border-gray-200 p-4 bg-gray-50">
                <h3 className="text-sm font-semibold text-brand-gray-dark mb-3">Preview en vivo</h3>
                <div className="flex items-center gap-3">
                  <PreviewIcon className="h-5 w-5" style={{ color: form.iconColor }} />
                  <span
                    className="px-3 py-1 rounded-full text-sm font-semibold border"
                    style={{
                      backgroundColor: form.bgColor,
                      color: form.textColor,
                      borderColor: 'rgba(0,0,0,0.08)',
                    }}
                  >
                    {form.labelEs || form.code || 'Tipo de evento'}
                  </span>
                </div>
                <p className={`text-xs mt-2 ${hasLowContrast ? 'text-red-600' : 'text-green-700'}`}>
                  Contraste estimado: {contrastRatio.toFixed(2)} {hasLowContrast ? '(bajo)' : '(ok)'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-gray-dark">Color de fondo</label>
                <div className="relative">
                  <div className="flex gap-2 items-center mb-2">
                    <button
                      type="button"
                      className="h-10 w-10 rounded border border-gray-300"
                      style={{ backgroundColor: form.bgColor }}
                      onClick={() => setOpenColorPicker((prev) => (prev === 'bgColor' ? null : 'bgColor'))}
                      title="Abrir selector de color"
                    />
                    <input
                      className={inputClassName}
                      value={form.bgColor}
                      onChange={(e) => setForm((p) => ({ ...p, bgColor: normalizeHex(e.target.value) }))}
                    />
                  </div>
                  {openColorPicker === 'bgColor' && (
                    <div className="absolute top-12 left-0 z-30 bg-white border border-gray-200 rounded-md p-2 shadow-lg">
                      <HexColorPicker color={form.bgColor} onChange={(color) => setForm((p) => ({ ...p, bgColor: color }))} />
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-gray-dark">Color de letra</label>
                <div className="relative">
                  <div className="flex gap-2 items-center mb-2">
                    <button
                      type="button"
                      className="h-10 w-10 rounded border border-gray-300"
                      style={{ backgroundColor: form.textColor }}
                      onClick={() => setOpenColorPicker((prev) => (prev === 'textColor' ? null : 'textColor'))}
                      title="Abrir selector de color"
                    />
                    <input
                      className={inputClassName}
                      value={form.textColor}
                      onChange={(e) => setForm((p) => ({ ...p, textColor: normalizeHex(e.target.value) }))}
                    />
                  </div>
                  {openColorPicker === 'textColor' && (
                    <div className="absolute top-12 left-0 z-30 bg-white border border-gray-200 rounded-md p-2 shadow-lg">
                      <HexColorPicker
                        color={form.textColor}
                        onChange={(color) => setForm((p) => ({ ...p, textColor: color }))}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-gray-dark">Color ícono</label>
                <div className="relative">
                  <div className="flex gap-2 items-center mb-2">
                    <button
                      type="button"
                      className="h-10 w-10 rounded border border-gray-300"
                      style={{ backgroundColor: form.iconColor }}
                      onClick={() => setOpenColorPicker((prev) => (prev === 'iconColor' ? null : 'iconColor'))}
                      title="Abrir selector de color"
                    />
                    <input
                      className={inputClassName}
                      value={form.iconColor}
                      onChange={(e) => setForm((p) => ({ ...p, iconColor: normalizeHex(e.target.value) }))}
                    />
                  </div>
                  {openColorPicker === 'iconColor' && (
                    <div className="absolute top-12 left-0 z-30 bg-white border border-gray-200 rounded-md p-2 shadow-lg">
                      <HexColorPicker
                        color={form.iconColor}
                        onChange={(color) => setForm((p) => ({ ...p, iconColor: color }))}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setForm(initialForm);
                  navigate('/platform/event-types');
                }}
                className="bg-brand-white text-brand-orange border border-brand-orange hover:bg-brand-orange hover:text-brand-white px-4 py-2 rounded-md transition-colors duration-200"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="bg-brand-orange text-brand-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors duration-200 disabled:opacity-50"
              >
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

const normalizeHex = (value: string): string => {
  if (!value.startsWith('#')) return `#${value}`.slice(0, 7);
  return value.slice(0, 7);
};

const getContrastRatio = (textColor: string, bgColor: string): number => {
  const textLum = getLuminance(textColor);
  const bgLum = getLuminance(bgColor);
  const lighter = Math.max(textLum, bgLum);
  const darker = Math.min(textLum, bgLum);
  return (lighter + 0.05) / (darker + 0.05);
};

const getLuminance = (hex: string): number => {
  const cleaned = hex.replace('#', '');
  const normalized =
    cleaned.length === 3
      ? cleaned
          .split('')
          .map((c) => c + c)
          .join('')
      : cleaned.padEnd(6, '0');
  const r = parseInt(normalized.slice(0, 2), 16) / 255;
  const g = parseInt(normalized.slice(2, 4), 16) / 255;
  const b = parseInt(normalized.slice(4, 6), 16) / 255;
  const channels = [r, g, b].map((channel) =>
    channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4)
  );
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
};
